import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";
import { Card } from "./ui/card";

const FAQS = [
  {
    q: "What's free and what's paid?",
    a: "Tutorials and the Code Simulator are completely free — no account required. The AI/ML Bootcamp is the only paid offering on the platform.",
  },
  {
    q: "Is this for beginners or experienced developers?",
    a: "Both. The tutorials start from absolute scratch (variables, loops, functions) and build up gradually. The Bootcamp takes you all the way to advanced AI/ML engineering.",
  },
  {
    q: "How is the Code Simulator different from just running code?",
    a: "Running code gives you output. The Code Simulator shows you the journey — it steps through every line, revealing live memory state, variable values, and the call stack so you truly understand what's happening under the hood.",
  },
  {
    q: "When does the AI Interview Platform launch?",
    a: "Soon. We're in active development targeting a Q3 2025 launch. Drop your email in the teaser section above and you'll be the first to get access — free for early users.",
  },
  {
    q: "Do I get a certificate for the Bootcamp?",
    a: "Yes. You receive a verified certificate of completion after finishing all 12 projects. It's designed to be shareable on LinkedIn and presented to employers as proof of your skills.",
  },
  {
    q: "Can I try before I buy?",
    a: "Absolutely. All tutorials and the Code Simulator are free forever. Explore them fully before deciding whether the Bootcamp is right for you — no pressure, no paywall on the free content.",
  },
];

const FAQItem = ({ item, index, isOpen, onToggle, monoFont, soraFont }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ delay: index * 0.07, duration: 0.4 }}
    style={{ borderBottom: '1px solid #1F2937' }}
    className="last:border-b-0"
  >
    <button
      onClick={onToggle}
      className="w-full flex items-start justify-between gap-6 py-8 text-left group transition-all"
    >
      <span style={monoFont} className={`text-[13px] font-black uppercase tracking-tight transition-all duration-300 pr-4 ${
        isOpen ? "text-[#00E676]" : "text-white group-hover:text-[#00E676]"
      }`}>
        {item.q}
      </span>

      <div style={{ 
        backgroundColor: isOpen ? '#00E67611' : '#111827', 
        borderColor: isOpen ? '#00E676' : '#1F2937' 
      }} className="w-8 h-8 flex items-center justify-center flex-shrink-0 border transition-all duration-300 mt-[-4px]">
        {isOpen ? <Minus className="w-4 h-4 text-[#00E676]" /> : <Plus className="w-4 h-4 text-[#6B7280]" />}
      </div>
    </button>

    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          key="answer"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3, ease: "circOut" }}
          className="overflow-hidden"
        >
          <p style={soraFont} className="pb-8 text-[#6B7280] text-sm leading-relaxed pr-12">
            {item.a}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  </motion.div>
);

const FAQSection = () => {
  const [openIdx, setOpenIdx] = useState(null);
  const toggle = (i) => setOpenIdx((prev) => (prev === i ? null : i));
  const monoFont = { fontFamily: "'JetBrains Mono', monospace" };
  const soraFont = { fontFamily: "'Sora', sans-serif" };

  return (
    <section style={{ backgroundColor: '#0A0F1E', ...soraFont }} className="relative py-32 overflow-hidden border-t border-[#1F2937]">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-[#00E676]/5 blur-[120px] rounded-full" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6">
        <motion.div className="text-center mb-20 flex flex-col gap-6">
          <div className="flex items-center justify-center gap-4">
            <div className="h-px w-8 bg-[#00E676]/20" />
            <span style={monoFont} className="text-[10px] font-black text-[#00E676] uppercase tracking-[0.4em]">Support Center</span>
            <div className="h-px w-8 bg-[#00E676]/20" />
          </div>

          <h2 style={monoFont} className="text-[clamp(2rem,5vw,4rem)] font-black text-white tracking-tighter leading-none uppercase">
            Platform <span className="text-[#00E676] italic">Knowledge Base</span>
          </h2>
          <p style={soraFont} className="text-[#6B7280] text-lg max-w-xl mx-auto">
            Everything you need to know about starting your Python journey.
          </p>
        </motion.div>

        <Card style={{ padding: '0 2.5rem' }} className="border-[#1F2937]">
          {FAQS.map((item, i) => (
            <FAQItem
              key={i}
              index={i}
              item={item}
              isOpen={openIdx === i}
              onToggle={() => toggle(i)}
              monoFont={monoFont}
              soraFont={soraFont}
            />
          ))}
        </Card>

        <motion.p style={monoFont} className="text-[#6B7280] text-[10px] font-black text-center mt-16 uppercase tracking-[0.3em]">
          Still have questions? <a href="/contact" className="text-[#00E676] hover:underline">Speak with an advisor →</a>
        </motion.p>
      </div>
    </section>
  );
};

export default FAQSection;
